import { z } from 'zod';
import { AVATAR_IMAGE_MAX_BYTES } from './constants';
import type { MediaType } from './content';

/** A title watched more than once, for "Most rewatched". */
export interface RewatchedTitle {
  mediaId: string;
  contentType: MediaType;
  title: string;
  posterUrl: string | null;
  /** Times watched to the end; for a series, its most watched episode. */
  times: number;
}

/** The numbers a shared stats link shows: no account, email or history details. */
export interface PublicProfileStats {
  name: string;
  /** Watch time in seconds. */
  watchSeconds: number;
  films: number;
  series: number;
  episodes: number;
  /** Times a film or series was watched again. */
  rewatches: number;
  topRewatched: RewatchedTitle[];
  downloads: number;
}

export interface ProfileStats extends PublicProfileStats {
  /** When rewatches and downloads started being counted, if after the profile was made. */
  countingSince: string | null;
  canShare: boolean;
  share: { url: string; since: string } | null;
}

export const shareStatsSchema = z.object({
  /** The avatar as the app draws it, shown on the shared page. */
  avatar: z
    .string()
    .max(Math.ceil((AVATAR_IMAGE_MAX_BYTES * 4) / 3) + 40)
    .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/, 'Use a PNG, JPEG or WebP image')
    .optional(),
});
export type ShareStatsInput = z.input<typeof shareStatsSchema>;

/** "42h 17m", "35m", "0m". */
export function formatWatchTime(seconds: number) {
  const m = Math.floor(Math.max(0, seconds) / 60);
  const h = Math.floor(m / 60);
  if (!h) return `${m}m`;
  return m % 60 ? `${h.toLocaleString('en-GB')}h ${m % 60}m` : `${h.toLocaleString('en-GB')}h`;
}
